import { useCallback, useState } from 'react';
import { MatrixSection } from './MatrixSection';
import { DNDSection } from './DNDSection';
import { MorningSummarySection } from './MorningSummarySection';
import { WarnThresholdsSection } from './WarnThresholdsSection';
import { RecipientsSection } from './RecipientsSection';
import { HistoryTimeline } from './HistoryTimeline';
import {
  useAlertConfig,
  useMatrixMutation,
  useDndMutation,
  useMorningSummaryMutation,
  useWarnThresholdsMutation,
  useCreateRecipientMutation,
  useUpdateRecipientMutation,
  useDeleteRecipientMutation,
  type AlertToastFn,
} from './useAlertConfig';
import './alertSettings.css';

// 권위: docs/ARCH_DASHBOARD.md §4.9 — 화면 9 컨테이너.
// 레이아웃: 좌측 6 섹션(Matrix / DND / Morning Summary / Warn Thresholds / Recipients / History),
//          우측 메타(updated_at / updated_by).
// 저장 성공/실패 토스트는 useAlertConfig 의 뮤테이션 onSuccess/onError 에서 발행.

interface ToastItem {
  id: string;
  msg: string;
  tone: 'info' | 'success' | 'error' | 'warn';
}

function fmtTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const pad = (n: number): string => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return iso;
  }
}

export default function AlertSettingsPage(): JSX.Element {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const pushToast: AlertToastFn = useCallback((msg, tone = 'info') => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, msg, tone }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4500);
  }, []);

  const configQ = useAlertConfig();
  const matrixM = useMatrixMutation(pushToast);
  const dndM = useDndMutation(pushToast);
  const summaryM = useMorningSummaryMutation(pushToast);
  const warnM = useWarnThresholdsMutation(pushToast);
  const createM = useCreateRecipientMutation(pushToast);
  const updateM = useUpdateRecipientMutation(pushToast);
  const deleteM = useDeleteRecipientMutation(pushToast);

  const recipientBusy =
    createM.isPending || updateM.isPending || deleteM.isPending;

  if (configQ.isLoading) {
    return (
      <div className="as-page">
        <div className="as-main">
          <div className="as-info">설정 로드 중…</div>
        </div>
      </div>
    );
  }
  if (configQ.isError || !configQ.data) {
    return (
      <div className="as-page">
        <div className="as-main">
          <div className="as-warn">
            설정 로드 실패: {(configQ.error as Error | null)?.message ?? 'unknown'}
          </div>
        </div>
      </div>
    );
  }

  const config = configQ.data;

  return (
    <div className="as-page">
      <div className="as-main">
        <MatrixSection
          config={config}
          onSave={(m) => matrixM.mutate(m)}
          isSaving={matrixM.isPending}
        />
        <DNDSection
          config={config}
          onSave={(d) => dndM.mutate(d)}
          isSaving={dndM.isPending}
        />
        <MorningSummarySection
          config={config}
          onSave={(s) => summaryM.mutate(s)}
          isSaving={summaryM.isPending}
        />
        <WarnThresholdsSection
          config={config}
          onSave={(w) => warnM.mutate(w)}
          isSaving={warnM.isPending}
        />
        <RecipientsSection
          config={config}
          onCreate={(body) => createM.mutate(body)}
          onUpdate={(id, body) => updateM.mutate({ recipientId: id, body })}
          onDelete={(id) => deleteM.mutate(id)}
          isBusy={recipientBusy}
        />
        <HistoryTimeline />
      </div>

      <aside className="as-side">
        <div className="as-card as-card--side">
          <h3 className="as-section__title">설정 메타</h3>
          <div>
            <div className="as-meta__label">updated_at</div>
            <div className="as-meta__value">{fmtTs(config.updatedAt)}</div>
          </div>
          <div>
            <div className="as-meta__label">updated_by</div>
            <div className="as-meta__value">{config.updatedBy ?? '—'}</div>
          </div>
          <div>
            <div className="as-meta__label">recipients</div>
            <div className="as-meta__value">
              {config.recipients.filter((r) => r.enabled).length}{' '}
              <span style={{ opacity: 0.6 }}>
                / {config.recipients.length}
              </span>
            </div>
          </div>
        </div>
      </aside>

      <div className="as-toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`as-toast as-toast--${t.tone}`}>
            {t.msg}
          </div>
        ))}
      </div>
    </div>
  );
}
